import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { TaskDialog } from "@/components/tasks/TaskDialog";

const EMPTY = {
  title: "",
  description: "",
  due_date: "",
  priority: "Mittel",
  status: "Offen",
  assigned_to_user_id: "",
  customer_id: "",
};

const noop = () => {};
const TaskEditorContext = createContext({ openCreate: noop, openEdit: noop, users: [], customers: [] });

export function useTaskEditor() {
  return useContext(TaskEditorContext);
}

export function TaskEditorProvider({ children }) {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => console.error("Benutzer konnten nicht geladen werden:", e));
    api.get("/customers").then((r) => setCustomers(r.data)).catch((e) => console.error("Kunden konnten nicht geladen werden:", e));
  }, []);

  const openCreate = useCallback((prefill = {}) => {
    setEditTask(null);
    setFormData({ ...EMPTY, ...prefill });
    setOpen(true);
  }, []);

  const openEdit = useCallback((task) => {
    setEditTask(task);
    setFormData({
      title: task.title || "",
      description: task.description || "",
      due_date: task.due_date || "",
      priority: task.priority || "Mittel",
      status: task.status || "Offen",
      assigned_to_user_id: task.assigned_to_user_id || "",
      customer_id: task.customer_id || "",
    });
    setOpen(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editTask) {
        await api.put(`/tasks/${editTask.id}`, formData);
        toast.success("Aufgabe aktualisiert");
      } else {
        await api.post("/tasks", formData);
        toast.success("Aufgabe erstellt");
      }
      setOpen(false);
      setEditTask(null);
      window.dispatchEvent(new Event("tasks-changed"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handlePlanWeek = useCallback(async (task, dateKey, scope = "mine") => {
    try {
      await api.post("/week-plan", { task_id: task.id, date: dateKey, scope });
      const label = new Date(`${dateKey}T00:00:00`).toLocaleDateString("de-DE", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      const where = scope === "team" ? "Team-Woche" : "Meine Woche";
      toast.success(`Eingeplant (${where}): ${label}`);
      window.dispatchEvent(new Event("tasks-changed"));
    } catch (error) {
      toast.error("Konnte nicht eingeplant werden");
    }
  }, [toast]);

  const value = useMemo(
    () => ({ openCreate, openEdit, users, customers }),
    [openCreate, openEdit, users, customers]
  );

  return (
    <TaskEditorContext.Provider value={value}>
      {children}
      <TaskDialog
        open={open}
        onOpenChange={setOpen}
        editTask={editTask}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        saving={saving}
        users={users}
        customers={customers}
        onPlanWeek={handlePlanWeek}
      />
    </TaskEditorContext.Provider>
  );
}
